if (!customElements.get('product-add-on')) {
  customElements.define(
    'product-add-on',
    class ProductAddOn extends HTMLElement {
      connectedCallback() {
        this.checkbox = this.querySelector('[data-product-add-on-checkbox]');
        this.messageWrapper = this.querySelector('[data-product-add-on-message-wrapper]');
        this.messageInput = this.querySelector('[data-product-add-on-message]');
        this.counter = this.querySelector('[data-product-add-on-counter]');
        this.form = document.getElementById(this.dataset.productFormId);

        if (!this.checkbox || !this.messageInput) return;

        this.onCheckboxChange = this.onCheckboxChange || (() => this.updateState());
        this.onMessageInput = this.onMessageInput || (() => this.updateCounter());
        this.onFormSubmit = this.onFormSubmit || this.handleFormSubmit.bind(this);

        this.checkbox.addEventListener('change', this.onCheckboxChange);
        this.messageInput.addEventListener('input', this.onMessageInput);

        // Capture the submit before the theme's normal single-item product-form
        // handler. When the add-on is not selected, the normal handler is left
        // completely untouched.
        this.form?.addEventListener('submit', this.onFormSubmit, true);
        this.updateState();
      }

      disconnectedCallback() {
        this.checkbox?.removeEventListener('change', this.onCheckboxChange);
        this.messageInput?.removeEventListener('input', this.onMessageInput);
        this.form?.removeEventListener('submit', this.onFormSubmit, true);
      }

      updateState() {
        const isSelected = this.selected;

        this.messageWrapper.hidden = !isSelected;
        this.messageInput.disabled = !isSelected;
        this.messageInput.required = isSelected;
        this.messageInput.setAttribute('aria-required', isSelected ? 'true' : 'false');

        if (!isSelected) this.messageInput.setCustomValidity('');
        this.updateCounter();

        if (isSelected) {
          window.requestAnimationFrame(() => this.messageInput.focus());
        }
      }

      updateCounter() {
        if (!this.counter) return;
        this.counter.textContent = `${this.messageInput.value.length} / ${this.messageInput.maxLength}`;
      }

      get selected() {
        return Boolean(this.checkbox?.checked && !this.checkbox.disabled);
      }

      get message() {
        return this.messageInput?.value.trim() || '';
      }

      reportMessageValidity() {
        if (!this.selected || !this.messageInput) return true;

        if (!this.message) {
          this.messageInput.setCustomValidity('Please enter the message to be written on the cake.');
        } else {
          this.messageInput.setCustomValidity('');
        }

        return this.messageInput.reportValidity();
      }

      handleFormSubmit(event) {
        if (!this.selected) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (this.isSubmitting || !this.reportMessageValidity()) return;
        this.submitWithAddOn();
      }

      buildCartPayload(formData) {
        const mainVariantId = Number(formData.get('id'));
        const addOnVariantId = Number(this.dataset.variantId);
        const quantity = Math.max(1, Number(formData.get('quantity')) || 1);

        if (!mainVariantId || !addOnVariantId || !this.message) return null;

        const inscriptionReference = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const mainProperties = {};

        for (const [key, value] of formData.entries()) {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (!propertyMatch || value === '') continue;
          if (typeof File !== 'undefined' && value instanceof File) continue;
          mainProperties[propertyMatch[1]] = value;
        }

        mainProperties['Cake inscription'] = this.message;
        mainProperties['_Inscription reference'] = inscriptionReference;
        mainProperties['_Has inscription add-on'] = 'true';

        const mainItem = {
          id: mainVariantId,
          quantity,
          properties: mainProperties,
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

        return {
          items: [
            mainItem,
            {
              id: addOnVariantId,
              quantity,
              properties: {
                'Cake inscription': this.message,
                '_Inscription reference': inscriptionReference,
                '_Inscription add-on': 'true',
                '_For product': this.dataset.parentProductTitle || '',
                '_For variant': String(mainVariantId),
              },
            },
          ],
        };
      }

      getSectionsToRender(cartDrawer, cartNotification, cartItems) {
        let sections = [];

        if (cartDrawer) {
          sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        }

        if (cartNotification) {
          window.loadTemplateContent?.(cartNotification);
          sections.push(...cartNotification.getSectionsToRender().map((section) => section.id));
        }

        if (cartItems) {
          sections.push(...cartItems.getSectionsToRender().map((section) => section.section));
        }

        return [...new Set(sections)];
      }

      setLoading(isLoading, productForm, submitButton) {
        const stickyButton = productForm?.ctaButton;

        if (isLoading) {
          submitButton?.setAttribute('aria-disabled', 'true');
          submitButton?.classList.add('loading');
          submitButton?.querySelector('.loading__spinner')?.classList.remove('hidden', 'spinning-complete');
          stickyButton?.setAttribute('aria-disabled', 'true');
          stickyButton?.classList.add('loading');
          stickyButton?.querySelector('.loading__spinner')?.classList.remove('hidden', 'spinning-complete');
          return;
        }

        submitButton?.removeAttribute('aria-disabled');
        submitButton?.classList.remove('loading');
        submitButton?.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');
        stickyButton?.removeAttribute('aria-disabled');
        stickyButton?.classList.remove('loading');
        stickyButton?.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');
      }

      async submitWithAddOn() {
        const productForm = this.form?.closest('product-form');
        const submitButton = productForm?.querySelector('[type="submit"]');
        const cartDrawer = document.querySelector('cart-drawer');
        const cartItems = document.querySelector('cart-items');
        const cartNotification = document.querySelector('cart-notification');
        const formData = new FormData(this.form);
        const payload = this.buildCartPayload(formData);

        if (!payload) {
          productForm?.handleErrorMessage?.('The inscription could not be added. Please refresh the page and try again.');
          return;
        }

        this.isSubmitting = true;
        productForm?.handleErrorMessage?.();
        this.setLoading(true, productForm, submitButton);

        const sections = this.getSectionsToRender(cartDrawer, cartNotification, cartItems);
        if (sections.length) {
          payload.sections = sections;
          payload.sections_url = window.location.pathname;
        }

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(payload);

          const response = await fetch(routes.cart_add_url, config);
          const result = await response.json();

          if (!response.ok || result.status) {
            const message = result.description || result.message || 'The inscription could not be added.';
            productForm?.handleErrorMessage?.(message);
            cartDrawer?.reload?.();
            return;
          }

          const cartAction = typeof theme !== 'undefined'
            ? theme.settings?.cartDrawer?.atcAction
            : null;

          if (cartAction === 'page') {
            window.location = window.routes.cart_url;
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: result,
            });
          }

          cartDrawer?.renderContents?.(result);
          cartItems?.renderContents?.(result);
          cartNotification?.renderContents?.(result);

          submitButton?.classList.add('added-to-cart');
          window.setTimeout(() => submitButton?.classList.remove('added-to-cart'), 1000);
        } catch (error) {
          console.error(error);
          productForm?.handleErrorMessage?.('The inscription could not be added. Please try again.');
        } finally {
          this.isSubmitting = false;
          this.setLoading(false, productForm, submitButton);

          if (cartDrawer?.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
          if (cartNotification?.classList.contains('is-empty')) cartNotification.classList.remove('is-empty');
          if (typeof publish === 'function') publish('quick-buy-action-complete');
        }
      }
    }
  );
}
